import { useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CityContext } from "@/contexts/city-context-provider";
import { ApiResponseError } from "@/services/web/use-api";
import { cn } from "@/lib/utils";
import { useSimulateParkingRate } from "../hooks/use-parking-rate-query";
import { SectionDivider } from "./section-divider";
import { ParkingRateType, PricingPointType } from "../payment-zone-area.type";
import { AnchorUnitEnum, RateTypeEnum } from "../pricing-configurator.types";
import { getDisplayName, getVersionLabel, toMajorUnit } from "../pricing-configurator.const";
import { RateSimulationRequest, RateSimulationResult } from "../rate-simulation.types";

type RateSimulationModalProps = {
  rate: ParkingRateType;
  onClose: () => void;
};

const UNIT_LABELS: Record<AnchorUnitEnum, { singular: string; plural: string }> = {
  [AnchorUnitEnum.MIN]: { singular: "minute", plural: "minutes" },
  [AnchorUnitEnum.DAY]: { singular: "jour", plural: "jours" },
  [AnchorUnitEnum.WEEK]: { singular: "semaine", plural: "semaines" },
  [AnchorUnitEnum.MONTH]: { singular: "mois", plural: "mois" },
  [AnchorUnitEnum.YEAR]: { singular: "an", plural: "ans" },
};

const unitLabel = (unit: AnchorUnitEnum | undefined, count: number) => {
  const labels = UNIT_LABELS[unit ?? AnchorUnitEnum.MIN];
  return count > 1 ? labels.plural : labels.singular;
};

const tierLabel = (point: PricingPointType) =>
  `${point.duration} ${unitLabel(point.unit, point.duration)} — ${toMajorUnit(point.priceAmount).toFixed(2)} €`;

const buildSimulationSchema = (isLong: boolean) =>
  z.object({
    startTime: z
      .string()
      .min(1, "La date de début est requise")
      .refine((value) => !Number.isNaN(new Date(value).getTime()), "Date de début invalide"),
    duration: isLong
      ? z.string().optional()
      : z
          .string()
          .min(1, "La durée est requise")
          .refine((value) => /^\d+$/.test(value) && Number(value) >= 1, "Nombre entier de minutes (minimum 1)"),
    tierIndex: isLong ? z.string().min(1, "La formule est requise") : z.string().optional(),
    licensePlate: z.string().trim().min(1, "La plaque d'immatriculation est requise"),
  });

type SimulationFormValues = z.infer<ReturnType<typeof buildSimulationSchema>>;

const formatSimulationError = (error: unknown): string => {
  if (error instanceof ApiResponseError && error.body && typeof error.body === "object") {
    const { message } = error.body as { message?: string | string[] };
    if (Array.isArray(message)) return message.join(". ");
    if (message) return message;
  }
  return "Une erreur est survenue lors de la simulation.";
};

const resolveTimeZone = (iana?: string): string | undefined => {
  if (!iana) return undefined;
  try {
    new Intl.DateTimeFormat("fr-FR", { timeZone: iana });
    return iana;
  } catch {
    return undefined;
  }
};

const formatDateTime = (iso: string, timeZone?: string) =>
  new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short", timeZone }).format(new Date(iso));

const formatRange = (fromIso: string, toIso: string, timeZone?: string) => {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  const day = new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeZone });
  const timeOnly = new Intl.DateTimeFormat("fr-FR", { timeStyle: "short", timeZone });
  const sameDay = day.format(from) === day.format(to);
  return sameDay
    ? `${day.format(from)} ${timeOnly.format(from)} – ${timeOnly.format(to)}`
    : `${formatDateTime(fromIso, timeZone)} – ${formatDateTime(toIso, timeZone)}`;
};

/** Wall-clock key ("yyyy-MM-ddTHH:mm") of an instant in the given timezone, comparable to a datetime-local value. */
const wallClockKey = (iso: string, timeZone: string) => {
  const parts = new Intl.DateTimeFormat("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).formatToParts(new Date(iso));
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
};

const formatMinutes = (totalMinutes: number) => {
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = Math.round(totalMinutes % 60);
  const segments: string[] = [];
  if (days) segments.push(`${days} j`);
  if (hours) segments.push(`${hours} h`);
  if (minutes || segments.length === 0) segments.push(`${minutes} min`);
  return segments.join(" ");
};

const PriceRow = ({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) => (
  <div className={cn("flex items-center justify-between text-sm", muted && "text-muted-foreground")}>
    <span>{label}</span>
    <span className="font-medium tabular-nums">{value}</span>
  </div>
);

const FlagBadge = ({ enabled, onLabel, offLabel }: { enabled: boolean; onLabel: string; offLabel: string }) => (
  <span
    className={cn(
      "rounded-full px-2 py-0.5 text-xs font-medium",
      enabled
        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
        : "bg-muted text-muted-foreground",
    )}
  >
    {enabled ? onLabel : offLabel}
  </span>
);

type SimulationRun = {
  request: RateSimulationRequest;
  result: RateSimulationResult;
};

const SimulationResult = ({ run, timeZone }: { run: SimulationRun; timeZone?: string }) => {
  const { request, result } = run;
  const currencyFormat = new Intl.NumberFormat("fr-FR", { style: "currency", currency: result.currency });
  const money = (minorAmount: number) => currencyFormat.format(toMajorUnit(minorAmount));

  const isShortRequest = request.unit === undefined;
  const durationChanged = isShortRequest && request.duration !== result.adjustedDuration;
  const startChanged = timeZone
    ? wallClockKey(result.adjustedStartTime, timeZone) !== request.startTime.slice(0, 16)
    : false;

  const totalMs = new Date(result.endTime).getTime() - new Date(result.adjustedStartTime).getTime();
  const segmentWidth = (segment: { from: string; to: string }) => {
    if (totalMs <= 0) return 100 / result.coverage.length;
    return ((new Date(segment.to).getTime() - new Date(segment.from).getTime()) / totalMs) * 100;
  };

  const rightCheck = result.parkingRight;

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <SectionDivider label="Prix" />
        <div className="space-y-1.5 rounded-lg border border-border bg-card px-4 py-3">
          <PriceRow label="Stationnement" value={money(result.amount)} />
          <PriceRow label="Frais de service (part client)" value={money(result.fees.customerShare)} />
          <div className="my-2 h-px bg-border" />
          <div className="flex items-center justify-between text-sm font-semibold">
            <span>Total payé par le client</span>
            <span className="tabular-nums">{money(result.amount + result.fees.customerShare)}</span>
          </div>
          <PriceRow
            label="Frais réglés avec la ville"
            value={`${money(result.fees.citySettlement)} (sur ${money(result.fees.total)} de frais)`}
            muted
          />
        </div>
        {result.fees.perRule.length > 1 && (
          <div className="rounded-lg border border-border px-4 py-2">
            <p className="mb-1 text-xs font-medium text-muted-foreground">Détail des frais par règle</p>
            {result.fees.perRule.map((rule) => (
              <div key={rule.ruleIndex} className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Règle {rule.ruleIndex + 1}</span>
                <span className="tabular-nums">
                  {money(rule.output)} dont {money(rule.customerShare)} part client
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <SectionDivider label="Session" />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <div className="rounded-lg border border-border px-3 py-2">
            <p className="text-xs text-muted-foreground">Début</p>
            <p className="text-sm font-medium">{formatDateTime(result.adjustedStartTime, timeZone)}</p>
          </div>
          <div className="rounded-lg border border-border px-3 py-2">
            <p className="text-xs text-muted-foreground">Fin</p>
            <p className="text-sm font-medium">{formatDateTime(result.endTime, timeZone)}</p>
          </div>
          <div className="rounded-lg border border-border px-3 py-2">
            <p className="text-xs text-muted-foreground">Durée facturée</p>
            <p className="text-sm font-medium">{formatMinutes(result.adjustedDuration)}</p>
          </div>
        </div>
        {(startChanged || durationChanged) && (
          <div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
            {startChanged && (
              <p>
                Le début demandé se trouve hors des heures payantes : la session commencerait le{" "}
                {formatDateTime(result.adjustedStartTime, timeZone)}.
              </p>
            )}
            {durationChanged && (
              <p>
                Durée ajustée : {formatMinutes(request.duration)} demandées → {formatMinutes(result.adjustedDuration)}{" "}
                facturées (palier ou durée maximale).
              </p>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <FlagBadge enabled={result.extendable} onLabel="Prolongeable" offLabel="Non prolongeable" />
          <FlagBadge enabled={result.stoppable} onLabel="Arrêtable" offLabel="Non arrêtable" />
        </div>
      </div>

      <div className="space-y-3">
        <SectionDivider label="Couverture" />
        <div className="flex h-2.5 w-full overflow-hidden rounded-full">
          {result.coverage.map((segment, index) => (
            <div
              key={index}
              style={{ width: `${segmentWidth(segment)}%` }}
              className={cn("min-w-[3px]", segment.kind === "paid" ? "bg-emerald-500" : "bg-muted-foreground/25")}
            />
          ))}
        </div>
        <div className="space-y-1">
          {result.coverage.map((segment, index) => (
            <div key={index} className="flex items-center gap-2 text-xs">
              <span
                className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  segment.kind === "paid" ? "bg-emerald-500" : "bg-muted-foreground/25",
                )}
              />
              <span className="text-muted-foreground">{formatRange(segment.from, segment.to, timeZone)}</span>
              <span className="font-medium">{segment.kind === "paid" ? "Payant" : "Gratuit"}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <SectionDivider label="Droit de stationnement" />
        {!rightCheck.checked ? (
          <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
            Vérification impossible{rightCheck.reason ? ` : ${rightCheck.reason}` : "."}
          </div>
        ) : (
          <div className="space-y-2">
            <FlagBadge
              enabled={rightCheck.hasRightAtStart === true}
              onLabel="Droit valide au début de la session"
              offLabel="Aucun droit valide au début de la session"
            />
            {rightCheck.rights.length === 0 ? (
              <p className="text-xs text-muted-foreground">Aucun droit trouvé pour cette plaque.</p>
            ) : (
              rightCheck.rights.map((right, index) => (
                <div key={index} className="rounded-lg border border-border px-4 py-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      {right.licensePlate}
                      {right.planId ? ` · ${right.planId}` : ""}
                    </span>
                    <FlagBadge enabled={right.activeAtStart} onLabel="Actif au début" offLabel="Inactif au début" />
                  </div>
                  {(right.validFrom || right.validTo) && (
                    <p className="mt-1 text-muted-foreground">
                      Validité : {right.validFrom ? formatDateTime(right.validFrom, timeZone) : "—"} →{" "}
                      {right.validTo ? formatDateTime(right.validTo, timeZone) : "—"}
                    </p>
                  )}
                  {right.zones && right.zones.length > 0 && (
                    <p className="text-muted-foreground">Zones : {right.zones.join(", ")}</p>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        {timeZone
          ? `Heures affichées dans le fuseau de la ville (${timeZone}).`
          : "Heures affichées dans le fuseau du navigateur."}
      </p>
    </div>
  );
};

export const RateSimulationModal = ({ rate, onClose }: RateSimulationModalProps) => {
  const { currentCity } = useContext(CityContext);
  const timeZone = resolveTimeZone(currentCity?.details?.timezone?.iana);

  const isLong = rate.rateType === RateTypeEnum.LONG;
  const tiers = useMemo(() => (isLong ? (rate.compiledPricingPoints ?? []) : []), [isLong, rate.compiledPricingPoints]);

  const { simulateParkingRate, loading } = useSimulateParkingRate(rate.id ?? "");
  const [run, setRun] = useState<SimulationRun | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<SimulationFormValues>({
    resolver: zodResolver(buildSimulationSchema(isLong)),
    defaultValues: {
      startTime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      duration: isLong ? "" : "60",
      tierIndex: isLong && tiers.length === 1 ? "0" : "",
      licensePlate: "",
    },
  });

  const runSimulation = async (values: SimulationFormValues) => {
    const tier = isLong ? tiers[Number(values.tierIndex)] : undefined;
    const request: RateSimulationRequest = {
      startTime: values.startTime,
      duration: tier ? tier.duration : Number(values.duration),
      licensePlate: values.licensePlate.trim(),
    };
    if (tier) {
      request.unit = tier.unit ?? AnchorUnitEnum.MIN;
    }

    setSubmitError(null);
    try {
      const result = await simulateParkingRate(request);
      setRun({ request, result });
    } catch (error) {
      setRun(null);
      setSubmitError(formatSimulationError(error));
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[90vh] flex-col p-0 sm:max-w-2xl">
        <div className="px-6 pt-6 pb-4 border-b border-border">
          <DialogHeader>
            <DialogTitle>
              Tester la tarification « {getDisplayName(rate)} » — {getVersionLabel(rate)}
            </DialogTitle>
            <DialogDescription>
              Simulez ce qu'un client paierait avec cette tarification, sans rien créer.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-hide px-6 py-6 space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(runSimulation)} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="startTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Début{timeZone ? ` (heure de la ville, ${timeZone})` : ""}{" "}
                        <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {isLong ? (
                  <FormField
                    control={form.control}
                    name="tierIndex"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Formule <span className="text-red-500">*</span>
                        </FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Sélectionnez une formule" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {tiers.map((tier, index) => (
                              <SelectItem key={index} value={String(index)}>
                                {tierLabel(tier)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <FormField
                    control={form.control}
                    name="duration"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Durée (minutes) <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input type="number" min={1} step={1} {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-end">
                <FormField
                  control={form.control}
                  name="licensePlate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Plaque d'immatriculation <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Ex. AB-123-CD" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" disabled={loading} className="sm:justify-self-end">
                  {loading ? "Simulation..." : "Simuler"}
                </Button>
              </div>
            </form>
          </Form>

          {submitError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive whitespace-pre-line">
              {submitError}
            </div>
          )}

          {run && <SimulationResult run={run} timeZone={timeZone} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};
